import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { CardACHSelection, CardACHSelectionView, MIN_ACH_AMOUNT } from '@frontend/payments-card-on-file';
import { PaymentsFlowType, StripeElementsWrapper } from '@frontend/payments-stripe-wrapper';
import { theme } from '@frontend/theme';
import { ContentLoader } from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { useUpdateRequestStatusPoll } from '../../../hooks/use-update-request-status-poll';
import { useFinalizeStepTrackingCallbacks, useInitiateStepTracking } from '../../../utils/step-tracking-store';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { CardOnFileNavigationSwitch } from './card-on-file-navigation-switch';
import { useCardOnFilePayment } from './card-on-file-stripe/use-card-on-file';
import { CardOnFileStepBodyProps } from './card-on-file-types';

export const CardOnFileStripe = () => {
  const { invoice } = useCollectPaymentMultiContext();
  return (
    <StripeElementsWrapper type={PaymentsFlowType.CardOnFilePayment} locationId={invoice?.locationId}>
      <CardOnFileNavigationSwitch body={CardOnFileStripeContent} />
    </StripeElementsWrapper>
  );
};

const styles = {
  body: css`
    .add-ach-form,
    .add-card-form {
      flex-grow: 1;
      height: 100%;
      padding: 0;

      &__footer {
        margin-top: auto;
      }
    }
  `,
  cardACHContent: css`
    padding: 0 !important;
    margin-bottom: ${theme.spacing(2)};
  `,
};

const CardOnFileStripeContent = ({ onPaymentSuccess, onCompleteStep, onGoBack }: CardOnFileStepBodyProps) => {
  const { t } = useTranslation('payments');

  const [selectedPM, setSelectedPM] = useState<PaymentMethod>();

  const [activeView, setActiveView] = useState<CardACHSelectionView>('payment-methods');

  const {
    invoice,
    invoiceQuery: { refetch: refetchInvoice },
    stripeData,
    activeGateway,
    flow,
  } = useCollectPaymentMultiContext();

  const { completeStepTracking, cancelStepTracking } = useFinalizeStepTrackingCallbacks({
    step: 'card-on-file',
    variant: 'card-on-file:complete-payment',
  });

  const onRefetchInvoice = useCallback(() => {
    return refetchInvoice().then((res) => res.data);
  }, [refetchInvoice]);

  const { makeCardOnFilePayment, makingCardOnFilePayment } = useCardOnFilePayment({
    paymentMethod: selectedPM,
    invoice,
    onInvoiceAlreadyPaid: onGoBack,
    onRefetchInvoice,
  });

  const { setProcessingAndStartPoll } = useUpdateRequestStatusPoll(invoice, () => {
    onPaymentSuccess();
  });

  const makePayment = useCallback(async () => {
    const workflowId = await makeCardOnFilePayment();

    /**
     * If card is declided, or failure happens, workflowId will be undefined.
     */
    if (workflowId) {
      setProcessingAndStartPoll();

      onCompleteStep();

      completeStepTracking();
    } else {
      refetchInvoice();
    }
  }, [makeCardOnFilePayment, onCompleteStep, setProcessingAndStartPoll, completeStepTracking]);

  const goBack = useCallback(() => {
    onGoBack();
    cancelStepTracking();
  }, [onGoBack, cancelStepTracking]);

  const onPrimaryClick = useCallback(async () => {
    await makePayment();
  }, [makePayment]);

  useInitiateStepTracking({
    activeGateway: 'stripe',
    flow,
    step: 'card-on-file',
    screenWidth: window.innerWidth,
    variant: 'card-on-file:complete-payment',
  });

  const { backTrackingId, completePaymentTrackingId } = useScopedTrackingIds({
    backTrackingId: `${PAYMENT_MULTI_STEPS.cardOnFile}--${activeGateway}--back-btn`,
    completePaymentTrackingId: `${PAYMENT_MULTI_STEPS.cardOnFile}--${activeGateway}--primary-btn`,
  });

  const isPaymentMethodsView = activeView === 'payment-methods';

  const label = useMemo(() => {
    return stripeData.enableACHOnFile
      ? PAYMENT_MULTI_STEP_LABELS['card-and-ach-on-file']
      : PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.cardOnFile];
  }, [stripeData.enableACHOnFile]);

  return (
    <StyledStep
      css={styles.body}
      id={PAYMENT_MULTI_STEPS.cardOnFile}
      label={label}
      isValid={!!selectedPM}
      header={{ title: label }}
      hasInternalFooter={!isPaymentMethodsView}
      footer={{
        onClickBack: goBack,
        onClickPrimary: onPrimaryClick,
        back: {
          trackingId: backTrackingId,
        },
        primary: {
          children: t('Complete Payment'),
          disabled: !selectedPM || makingCardOnFilePayment,
          trackingId: completePaymentTrackingId,
        },
      }}
      components={{
        Footer: isPaymentMethodsView ? undefined : () => null,
      }}
    >
      <CardACHSelection
        locationId={invoice?.locationId}
        patientId={invoice?.person.id}
        patientEmail={invoice?.person.emailAddress || ''}
        selectedPM={selectedPM}
        onChangePM={setSelectedPM}
        paymentAmount={invoice?.billedAmount}
        minACHAmount={MIN_ACH_AMOUNT}
        addCardContent={<CardACHSelection.AddCardForm />}
        addACHContent={<CardACHSelection.AddACHForm />}
        onChangeView={setActiveView}
      >
        <ContentLoader
          show={makingCardOnFilePayment}
          message={
            selectedPM?.type === 'us_bank_account'
              ? t('Making ACH on File Payment...')
              : t('Making Card on File Payment...')
          }
        />

        <CardACHSelection.Content css={styles.cardACHContent} />
      </CardACHSelection>
    </StyledStep>
  );
};
